import { Separator } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPSpinner, IPSpinnerSize, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ClassificationTagList } from 'components/Classification/ClassificationTagList';
import HrefLink from 'components/Links/HrefLink';
import RichTextReadOnly from 'components/Text/RichTextReadOnly';
import {
  globalStackTokensGapExtraSmall,
  globalStackTokensGapLargePlus5,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import { Owner } from 'models/owner';
import ProcessingActivity, { PrivacyFramework } from 'models/processingActivity';
import { ProcessingActivityContext } from 'models/processingActivityContext';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toLocaleDateNumeric } from 'utils/datetime';

interface IProcessingActivityReportContentProps {
  report?: ProcessingActivity;
  reportContext?: ProcessingActivityContext;
  isLoading: boolean;
}

const FieldValue = (props: { label: string; value: string | number | undefined }) => {
  return (
    <IPStack horizontal tokens={globalStackTokensGapSmall}>
      <IPStack.Item styles={{ root: { minWidth: 250, maxWidth: 250 } }}>
        <IPText variant="mediumPlus" styles={globalTextStylesBold}>
          {props.label}:
        </IPText>
      </IPStack.Item>
      <IPStack.Item>
        <IPText variant="mediumPlus">{props.value || '-'}</IPText>
      </IPStack.Item>
    </IPStack>
  );
};

const FieldValueHtml = (props: { label: string; value: string | number | undefined }) => {
  return (
    <IPStack tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPText variant="mediumPlus" styles={globalTextStylesBold}>
          {props.label}:
        </IPText>
      </IPStack.Item>
      <IPStack.Item>
        <RichTextReadOnly html={props.value?.toString() || '-'} />
      </IPStack.Item>
    </IPStack>
  );
};

const FieldValueJsx = (props: { label: string; value: JSX.Element | undefined }) => {
  return (
    <IPStack horizontal tokens={globalStackTokensGapSmall}>
      <IPStack.Item styles={{ root: { minWidth: 250, maxWidth: 250 } }}>
        <IPText variant="mediumPlus" styles={globalTextStylesBold}>
          {props.label}:
        </IPText>
      </IPStack.Item>
      <IPStack.Item>{props.value || '-'}</IPStack.Item>
    </IPStack>
  );
};

export const ProcessingActivityReportContent = (props: IProcessingActivityReportContentProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'processingActivity', 'processingActivities', 'reports']);
  const privacyFramework =
    (appContext.globalDataCache.settings.get('PrivacyFramework') as PrivacyFramework) ?? PrivacyFramework.GDPR;

  if (props.isLoading) {
    return (
      <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!props.report || !props.reportContext) {
    return (
      <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
        <IPText>{t('translation:General.Notifications.NotAvailable')}</IPText>
      </IPStack>
    );
  }

  const report = props.report;
  const reportContext = props.reportContext;
  const owner = new Owner(report, appContext);

  return (
    <IPStack tokens={globalStackTokensGapLargePlus5}>
      <IPStack horizontalAlign="center">
        <IPText variant="xxLargePlus">
          <HrefLink href={`/processingactivity/${report.processingActivityId}`} external underline>
            {t('reports:Report20.Name')}
          </HrefLink>
        </IPText>
      </IPStack>
      <Separator styles={{ root: { height: 1 } }} />
      <IPStack tokens={globalStackTokensGapExtraSmall}>
        <FieldValue label={t('processingActivity:TabDetails.Fields.Name.Label')} value={report.name} />
        <FieldValue label={t('processingActivity:TabDetails.Fields.Code.Label')} value={report.code} />
        <FieldValue label={t('processingActivity:TabDetails.Fields.Owner.Label')} value={owner.name} />
        <FieldValue label={t('processingActivity:TabDetails.Fields.Role')} value={report.getRoleText(t)} />
        <FieldValue label={t('processingActivity:TabDetails.Fields.State')} value={report.getStateText(t)} />
        <FieldValue
          label={t('processingActivity:TabDetails.Fields.StartDate')}
          value={report.startDate ? toLocaleDateNumeric(report.startDate) : undefined}
        />
        <FieldValue
          label={t('processingActivity:TabDetails.Fields.EndDate')}
          value={report.endDate ? toLocaleDateNumeric(report.endDate) : undefined}
        />
        <FieldValue
          label={t('processingActivity:TabDetails.Fields.RetentionPeriod')}
          value={report.retentionPeriod?.value}
        />
        <FieldValue
          label={ProcessingActivity.getTransfersTextLabel(t, privacyFramework)}
          value={report.getTransfersText(t, privacyFramework)}
        />
      </IPStack>
      <Separator styles={{ root: { height: 1 } }} />
      <IPText variant="xLarge">{t('processingActivity:TabDetails.Pivot.Purpose')}</IPText>
      <FieldValueHtml label={t('processingActivity:TabDetails.Fields.Purpose.Label')} value={report.purpose} />
      <FieldValueHtml label={t('processingActivity:TabDetails.Fields.Description.Label')} value={report.description} />
      <Separator styles={{ root: { height: 1 } }} />
      <IPText variant="xLarge">{t('processingActivity:TabDetails.Pivot.Data')}</IPText>
      <IPStack tokens={globalStackTokensGapExtraSmall}>
        <FieldValue
          label={t('processingActivity:ReportFields.Assets')}
          value={report.data?.map((d) => d.name).join(', ')}
        />
        <FieldValue
          label={t('processingActivity:TabDetails.StringValueLabels.DataCategories')}
          value={report.dataCategories?.map((d) => d.value).join(', ')}
        />
        <FieldValueJsx
          label={t('processingActivities:TabOverview.Column.Classifications')}
          value={<ClassificationTagList compact classifications={report.getDataClassifications()} />}
        />
        <FieldValue
          label={t('processingActivity:TabDetails.StringValueLabels.DataSubjects')}
          value={report.dataSubjects?.map((d) => d.value).join(', ')}
        />
        <FieldValue
          label={t('processingActivity:TabDetails.Fields.DataSubjectScale')}
          value={report.dataSubjectScale?.value}
        />
        <FieldValue
          label={t('processingActivity:TabDetails.StringValueLabels.LegalBasis')}
          value={report.legalBasis?.map((d) => d.value).join(', ')}
        />
      </IPStack>
      <Separator styles={{ root: { height: 1 } }} />
      <IPText variant="xLarge">{t('processingActivity:TabDetails.Pivot.Roles')}</IPText>
      <IPStack tokens={globalStackTokensGapExtraSmall}>
        <FieldValue
          label={t('processingActivity:ReportFields.Controllers')}
          value={report.joinControllers?.map((c) => c.name).join(', ')}
        />
        <FieldValue
          label={t('processingActivity:ReportFields.Processors')}
          value={report.processors?.map((p) => p.name).join(', ')}
        />
        <FieldValue
          label={t('processingActivity:ReportFields.Recipients')}
          value={report.recipients?.map((r) => r.name).join(', ')}
        />
        <FieldValue
          label={t('processingActivity:ReportFields.RecipientsCategories')}
          value={report.recipientCategories?.map((r) => r.value).join(', ')}
        />
        <FieldValue
          label={t('processingActivity:TabDetails.StringValueLabels.ExecutingEntities')}
          value={report.executingEntities?.map((r) => r.value).join(', ')}
        />
      </IPStack>
      <Separator styles={{ root: { height: 1 } }} />
      <IPText variant="xLarge">{t('processingActivity:ReportFields.OrganizationalMeasures')}</IPText>
      <IPStack tokens={globalStackTokensGapExtraSmall}>
        <FieldValue
          label={t('processingActivity:ReportFields.Risks')}
          value={reportContext.risks.map((c) => c.name).join(', ')}
        />
        <FieldValue
          label={t('processingActivity:ReportFields.Controls')}
          value={reportContext.controls?.map((p) => p.name).join(', ')}
        />
      </IPStack>
    </IPStack>
  );
};
